<template>
  <div class="col-span-full flex flex-col gap-8 md:gap-14 pb-8 md:col-span-7">
    <div class="flex flex-col gap-4">
      <h1 class="text-t1">Intresseanmälningar och erbjudanden</h1>
      <p class="text-preamble">
        Här kan du se dina intresseanmälningar och hålla dig uppdaterad på vad som händer med dem.
        Du kan även skapa bevakningar för objekt du är intresserad av. Kolla gärna in vår guide
        kring att söka bostad hos oss!
      </p>
      <div class="flex flex-col gap-4 md:flex-row">
        <a
          href="https://www.gavlegardarna.se/hyr-bostad/sa-gar-det-till-att-hyra-bostad/"
          class="group flex justify-between gap-6 rounded-[4px] bg-white px-4 py-3 font-thick text-black ring-1 ring-gray-light hover:ring-2 items-center"
          target="_blank"
        >
          Så går det till att hyra hos oss
          <span class="flex items-center justify-center w-8 h-8 rounded-full group-hover:bg-turquoise-10">
            <IconArrowRight size="24" class="text-dark-turquoise" />
          </span>
        </a>
      </div>
    </div>

    <div v-if="isLoading" class="flex flex-col gap-4">
      <CardLoaderSmall class="flex-1 flex-col p-0" />
    </div>

    <div v-else-if="currentOffers.length > 0">
      <h2 class="text-t4 mb-6">
        Du har {{ currentOffers.length === 1 ? 'ett' : currentOffers.length }} aktuell{{
          currentOffers.length === 1 ? 't' : 'a'
        }}
        erbjudande{{ currentOffers.length === 1 ? '' : 'n' }}
      </h2>
      <OfferItem
        v-for="(offer, index) in currentOffers"
        :key="offer.rentalObjectCode"
        :offer="offer"
      />
    </div>

    <Box v-else class="!rounded-[10px]">
      <h2 class="text-t4">Erbjudanden</h2>
      <div class="flex min-h-28 flex-1 flex-col items-center justify-center gap-2">
        <IconBoxEmptyBlank />
        <p class="text-center">Du har inga erbjudanden just nu</p>

        </div>
        <div class="mt-4" v-if="showOldOffers">
          <OfferItemOlder
            v-for="(offer, index) in oldOffers"
            :key="offer.rentalObjectCode"
            :offer="offer"
          />
        </div>

      <div class="flex flex-1 flex-col items-center justify-center gap-2">
        <Button class="mt-2" variant="tertiary" size="large" @click="showOldOffers=!showOldOffers" v-if="oldOffers.length > 0">
          {{ showOldOffers ? 'Dölj historik' : 'Visa historik (' + oldOffers.length  + ')' }}
          <template #icon-after>
            <IconChevronUp v-if="showOldOffers" />
            <IconChevronDown v-else />
          </template>
        </Button>
      </div>
    </Box>

    <div v-if="isLoading" class="flex flex-col gap-4">
      <CardLoaderSmall class="flex-1 flex-col p-0" />
    </div>

    <div v-else-if="currentNoteofinterests.length > 0" class="flex flex-col gap-8">
      <h2 class="text-t4">Intresseanmälningar</h2>
      <NoteInterestItem
        v-for="(noteofinterest, index) in currentNoteofinterests"
        :noteofinterest="noteofinterest"
      />
    </div>
    <Box v-else class="!rounded-[10px]">
      <h2 class="text-t4">Intresseanmälningar</h2>
      <div class="flex flex-col gap-2 py-4">
        <h3 class="text-t6">Du har inga aktiva intresseanmälningar just nu</h3>
        <p class="text-b2 max-w-md">
          Börja med att hitta en bostad, en parkeringsplats eller ett förråd som intresserar dig i
          vår tjänst sök ledigt.
        </p>
      </div>
      <Button size="large" class="w-fit" href="/sok-ledigt">
        Gå till sök ledigt
        <template #icon-after>
          <IconArrowRight color="white" size="22" />
        </template>
      </Button>
    </Box>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Modal from '@/components/ui/modal';
import dayjs from 'dayjs';
import Box from '@/components/ui/box';
import Button from '@/components/ui/button';
import IconTrashCan from '@/components/icons/IconTrashCan';
import IconArrowRight from '@/components/icons/IconArrowRight';
import IconChevronDown from '@/components/icons/IconChevronDown';
import IconChevronUp from '@/components/icons/IconChevronUp';
import IconBoxEmptyBlank from '@/components/icons/IconBoxEmptyBlank';
import OfferItem from '@/pages/offers/offer-item';
import OfferItemOlder from '@/pages/offers/offer-item-older';
import NoteInterestItem from '@/pages/offers/note-interest-item.vue';
import CardLoaderSmall from '@/components/loader/CardLoaderSmall';

export default {
  components: {
    Box,
    Modal,
    Button,
    NoteInterestItem,
    OfferItem,
    IconTrashCan,
    IconArrowRight,
    IconChevronDown,
    IconChevronUp,
    IconBoxEmptyBlank,
    CardLoaderSmall,
    OfferItemOlder
  },
  data() {
    return {
      showModal: {
        howItWorks: false,
        rulesForInterest: false,
      },
      showOldOffers: false,
      showOfferInfo: false,
      isLoading: true,
      // testAlwaysLoading: true,
    };
  },

  computed: {
    ...mapGetters([
      'currentOffers',
      'oldOffers',
      'currentNoteofintersts',
      'noteofinterests',
      'webapp_config',
    ]),

    objectCodeInfo() {
      return this.webapp_config.msg.note_of_intresest_info_from_admin_apartment;
    },

    currentNoteofinterests() {
      return this.noteofinterests.filter((note) => {
        return !this.currentOffers.find((offer) => {
          return offer.rentalObject.rentalObjectCode === note.rentalObjectCode;
        });
      });
    },
  },
  methods: {},
  created() {
    if (!!this.authStatus && (!this.profile || (this.profile && !this.profile.contactCode))) {
      this.$store.dispatch('fetchProfile');
    }
    // Skrolla alltid till toppen
    window.scroll(0, 0);

    if (!this.testAlwaysLoading) {
      new Promise((resolve) => setTimeout(resolve, 500)).then(() => {
        return Promise.all([
          this.$store.cache.dispatch('fetchOffers'),
          this.$store.cache.dispatch('fetchNoteofinterests'),
        ]).finally(() => {
          this.offersLoaded = true;
          this.isLoading = false;
        });
      });
    }
  },
};
</script>
